import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { clearSupabaseAuthStorage, createSupabaseAuthStorage } from './authStorage.js';

// NOTE: This browser module is sometimes pulled into Vercel Node serverless
// functions via shared util imports (e.g. src/utils/publicMarketingConfig.ts).
// In a non-Vite (Node) runtime `import.meta.env` is `undefined`, so a bare
// `import.meta.env.VITE_SUPABASE_URL` access throws at module load and crashes
// the whole function (FUNCTION_INVOCATION_FAILED). The `import.meta.env && ...`
// short-circuit keeps Vite's static replacement intact for the browser build
// while safely yielding `undefined` (no throw) under Node.
const supabaseUrl = import.meta.env && import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env && import.meta.env.VITE_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

/** Supabase project URL for direct fetch calls (e.g. edge functions) to read error bodies. */
export const getSupabaseUrl = () => supabaseUrl ?? '';
/** Anon key for edge function calls when JWT is not required or as fallback. */
export const getSupabaseAnonKey = () => supabaseAnonKey ?? '';

const missingConfigError = new Error(
  'Supabase environment variables are missing. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
);

const createRejectedPromise = <T = never>() =>
  Promise.resolve().then<T>(() => {
    throw missingConfigError;
  });

const createQueryBuilderProxy = () => {
  const handler: ProxyHandler<any> = {
    get(_target, prop) {
      if (prop === 'then') {
        return (onFulfilled?: (value: any) => any, onRejected?: (reason: any) => any) =>
          createRejectedPromise().then(onFulfilled, onRejected);
      }
      if (prop === 'catch') {
        return (onRejected?: (reason: any) => any) => createRejectedPromise().catch(onRejected);
      }
      if (prop === 'finally') {
        return (onFinally?: () => any) => createRejectedPromise().finally(onFinally);
      }

      const terminalMethods = new Set(['maybeSingle', 'single', 'returns', 'abortSignal', 'throwOnError']);

      if (typeof prop === 'string' && terminalMethods.has(prop)) {
        return () => createRejectedPromise();
      }

      const chainableMethods = new Set([
        'select',
        'insert',
        'update',
        'upsert',
        'delete',
        'eq',
        'neq',
        'gt',
        'gte',
        'lt',
        'lte',
        'like',
        'ilike',
        'is',
        'in',
        'contains',
        'containedBy',
        'overlaps',
        'textSearch',
        'match',
        'order',
        'limit',
        'range',
        'or',
        'filter',
      ]);

      if (typeof prop === 'string' && chainableMethods.has(prop)) {
        return () => createQueryBuilderProxy();
      }

      return () => createQueryBuilderProxy();
    },
    apply() {
      return createQueryBuilderProxy();
    },
  };

  return new Proxy(() => undefined, handler);
};

const createUnavailableSupabaseClient = (): SupabaseClient => {
  if (typeof window !== 'undefined') {
    console.warn(missingConfigError.message);
  }

  return new Proxy(
    {},
    {
      get(_target, prop) {
        if (prop === 'auth') {
          return {
            getSession: () => createRejectedPromise(),
            refreshSession: () => createRejectedPromise(),
            getUser: () => createRejectedPromise(),
            updateUser: () => createRejectedPromise(),
            signInWithPassword: () => createRejectedPromise(),
            signInWithOAuth: () => createRejectedPromise(),
            signUp: () => createRejectedPromise(),
            resetPasswordForEmail: () => createRejectedPromise(),
            signOut: () => createRejectedPromise(),
            onAuthStateChange: () => ({
              data: { subscription: { unsubscribe: () => {} } },
              error: missingConfigError,
            }),
          };
        }

        if (prop === 'from') {
          return () => createQueryBuilderProxy();
        }

        if (prop === 'rpc') {
          return () => createRejectedPromise();
        }

        if (prop === 'functions') {
          return {
            invoke: () => createRejectedPromise(),
          };
        }

        if (prop === 'storage') {
          return {
            from: () => ({
              upload: () => createRejectedPromise(),
              remove: () => createRejectedPromise(),
              download: () => createRejectedPromise(),
              update: () => createRejectedPromise(),
              list: () => createRejectedPromise(),
              getPublicUrl: () => ({
                data: { publicUrl: null },
                error: missingConfigError,
              }),
            }),
          };
        }

        if (prop === 'removeChannel' || prop === 'removeAllSubscriptions') {
          return () => {};
        }

        if (prop === 'channel') {
          return () => ({
            subscribe: () => createRejectedPromise(),
            on: () => ({
              subscribe: () => createRejectedPromise(),
            }),
          });
        }

        return () => createRejectedPromise();
      },
    },
  ) as SupabaseClient;
};

// Custom fetch to prevent caching of Supabase data (addons, services, etc.)
// Ensures public pages like /addon-services always show fresh DB content
const noCacheFetch: typeof fetch = (input, init) => {
  const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
  const isDataRequest = url.includes('/rest/v1/') || url.includes('/storage/v1/');
  if (!isDataRequest) return fetch(input, init);
  const opts: RequestInit = { ...init, cache: 'no-store' };
  const headers = new Headers(init?.headers);
  if (input instanceof Request) input.headers.forEach((v, k) => headers.set(k, v));
  headers.set('Cache-Control', 'no-cache, no-store, must-revalidate');
  headers.set('Pragma', 'no-cache');
  opts.headers = headers;
  return fetch(input, opts);
};

// Mobile-safe auth storage: prefer localStorage; migrate legacy Supabase keys.
const supabaseAuthStorage = createSupabaseAuthStorage(supabaseUrl);

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl!, supabaseAnonKey!, {
      global: { fetch: noCacheFetch },
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'supabase-auth-token',
        flowType: 'pkce',
        storage: supabaseAuthStorage,
      },
    })
  : createUnavailableSupabaseClient();

export { clearSupabaseAuthStorage };

export const supabaseConfigError = isSupabaseConfigured ? null : missingConfigError;

// Single source of session check: AuthContext. Do not call getSession() here to avoid
// double session check on load and full-page reload on stale token (which felt like stuck login).
supabase.auth.onAuthStateChange((event) => {
  if (event === 'TOKEN_REFRESHED' && import.meta.env && import.meta.env.DEV) {
    console.log('Token refreshed successfully');
  }
  // Don't log SIGNED_OUT here - it fires on invalid refresh token too and clutters console
});

export interface Profile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  whatsapp_phone?: string | null;
  role: 'admin' | 'supervisor' | 'customer' | 'employee';
  address: string | null;
  city: string | null;
  zip_code: string | null;
  created_at: string;
  updated_at: string;
}

export interface Employee {
  id: string;
  employee_id: string;
  user_id: string | null;
  department: string;
  hire_date: string;
  hourly_rate: number;
  is_active: boolean;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relationship: string | null;
  ot1_multiplier: number;
  ot2_multiplier: number;
  created_at: string;
  updated_at: string;
  profile?: Profile;
}

export interface Service {
  id: string;
  name: string;
  category: string;
  description: string | null;
  base_price: number;
  duration_minutes: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Booking {
  id: string;
  booking_number: string;
  customer_id: string;
  service_id: string | null;
  service_type: string;
  booking_date: string;
  booking_time: string;
  address: string;
  total_amount: number;
  status:
    | 'scheduled'
    | 'in-progress'
    | 'in_progress'
    | 'completed'
    | 'cancelled'
    | 'rescheduled'
    | 'completed_ready_to_invoice'
    | 'ready_for_advance_invoice';
  assigned_to: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  customer?: Profile;
  service?: Service;
  employee?: Employee;
  estimated_duration?: number | null;
  duration_is_manual?: boolean | null;
  pricing_tier?: string | { display_label?: string | null; duration_hours?: number | null } | null;
  customer_info?: {
    first_name?: string;
    last_name?: string;
    [key: string]: any;
  } | null;
  additional_services?: any[] | null;
  addons?: any[] | null;
  is_custom?: boolean | null;
  is_custom_service?: boolean | null;
  line_items?: any[] | null;
  assigned_team?: {
    supervisor?: { first_name?: string; last_name?: string };
    [key: string]: any;
  } | null;
  assigned_team_id?: string | null;
  payment_status?: string | null;
  last_update_sent_at?: string | null;
  removed_at?: string | null;
  [key: string]: any;
}

export interface Attendance {
  id: string;
  employee_id: string;
  attendance_date: string;
  check_in: string | null;
  check_out: string | null;
  /** Date the check_out time falls on. Null means the same day as attendance_date. */
  check_out_date: string | null;
  hours_worked: number;
  ot_hours: number;
  status: 'present' | 'absent' | 'leave' | 'holiday';
  notes: string | null;
  weekly_bonus_days?: number | null;
  created_at: string;
  updated_at: string;
  employee?: Employee;
}

export interface Expense {
  id: string;
  category: string;
  description: string;
  amount: number;
  expense_date: string;
  payment_method: string | null;
  receipt_url: string | null;
  recorded_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Enquiry {
  id: string;
  name: string;
  email: string;
  phone: string;
  service_type: string | null;
  message: string | null;
  status: 'new' | 'contacted' | 'qualified' | 'converted' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to: string | null;
  follow_up_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface TeamMember {
  id: string;
  employee_id: string | null;
  name: string;
  role: 'supervisor' | 'cleaner' | 'specialist';
  skills: string[];
  is_available: boolean;
  rating: number;
  created_at: string;
  updated_at: string;
}
